import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Save, MessageSquare, UserCheck } from 'lucide-react';
import { Dispute, updateDispute } from '../services/disputeService';
import { formatDisputeReason } from '../lib/disputeUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dispute: Dispute & { id: string };
  tenantId: string;
  onSave: () => void;
}

export const DisputeEditModal: React.FC<Props> = ({ isOpen, onClose, dispute, tenantId, onSave }) => {
  const [correctedAmountInput, setCorrectedAmountInput] = useState<string>(
    dispute.correctedAmount !== undefined ? String(dispute.correctedAmount) : '0'
  );
  const [reasonInput, setReasonInput] = useState<string>(formatDisputeReason(dispute.reason));
  const [managerNotesInput, setManagerNotesInput] = useState<string>(
    dispute.managerNotes || (dispute as any).managerComment || ''
  );
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (dispute) {
      setCorrectedAmountInput(dispute.correctedAmount !== undefined ? String(dispute.correctedAmount) : '0');
      setReasonInput(formatDisputeReason(dispute.reason));
      setManagerNotesInput(dispute.managerNotes || (dispute as any).managerComment || '');
    }
  }, [dispute]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const parsedAmount = parseFloat(correctedAmountInput);
      const employeeComment = dispute.employeeNotes || dispute.explanation || (dispute as any).description || (dispute as any).notes || '';
      await updateDispute(tenantId, dispute.id, {
        correctedAmount: isNaN(parsedAmount) ? 0 : parsedAmount,
        reason: reasonInput,
        managerNotes: managerNotesInput,
        managerComment: managerNotesInput,
        employeeNotes: employeeComment,
        explanation: employeeComment // keep original explanation intact
      });
      onSave();
      onClose();
    } catch (err) {
      console.error('Error updating dispute:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const employeeComment = dispute.employeeNotes || dispute.explanation || (dispute as any).description || (dispute as any).notes;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-lg shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-black text-gray-900 tracking-tight">Edit Dispute Amount & Notes</h3>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  Adjust the approved tip amount for <span className="font-bold text-gray-800">{dispute.staffName || dispute.email}</span>
                </p>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div>
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Original Recorded</span>
                  <span className="text-base font-bold text-gray-600">${dispute.originalAmount.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest block">Adjustment Type</span>
                  <span className="text-xs font-bold text-gray-700">{dispute.type || 'Allocation Correction'}</span>
                </div>
              </div>

              {/* Employee's Original Comment (Read-Only) */}
              {employeeComment && (
                <div className="p-4 bg-amber-50/80 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-1.5 text-amber-800 text-[11px] font-black uppercase tracking-wider mb-1.5">
                    <MessageSquare size={14} className="shrink-0" />
                    <span>Employee Note ({dispute.staffName || 'Staff Member'}):</span>
                  </div>
                  <p className="text-xs text-amber-950 font-medium whitespace-pre-wrap leading-relaxed bg-white/70 p-2.5 rounded-lg border border-amber-100">
                    "{employeeComment}"
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Approved / Corrected Amount ($) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400 font-bold">$</span>
                  <input 
                    type="number" 
                    step="0.01" 
                    min="0"
                    required
                    value={correctedAmountInput} 
                    onChange={e => setCorrectedAmountInput(e.target.value)} 
                    className="input-field w-full pl-8 font-mono text-lg font-bold text-emerald-700" 
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Dispute Reason</label>
                <input 
                  type="text" 
                  value={reasonInput} 
                  onChange={e => setReasonInput(e.target.value)} 
                  className="input-field w-full" 
                  list="standard-reasons"
                />
                <datalist id="standard-reasons">
                  <option value="Calculated Tip Discrepancy" />
                  <option value="Missing from Schedule" />
                  <option value="Shift Hours Inaccurate" />
                  <option value="Missing Shift Entry" />
                  <option value="Tip Allocation Correction" />
                  <option value="Tip Submission Correction" />
                  <option value="Data Entry Error" />
                  <option value="POS Tip Override" />
                </datalist>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <UserCheck size={14} className="text-indigo-600" />
                  <span>Manager Review / Resolution Note</span>
                </label>
                <textarea 
                  value={managerNotesInput} 
                  onChange={e => setManagerNotesInput(e.target.value)} 
                  className="input-field w-full min-h-[90px] border-indigo-200 focus:border-indigo-500" 
                  rows={3} 
                  placeholder="Add manager explanation or reason for amount adjustment (this will be preserved alongside the employee note)..."
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={isSaving}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-600/20 transition-all"
                >
                  <Save size={16} /> {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

